import React, { useState, useEffect } from 'react';
import apiClient, { permissionMessage } from '../config/axiosConfig';

interface AddBikeProps {
  onBikeAdded: () => void;
  onCancel: () => void;
  /** Alleen de locaties die deze beheerder mag beheren. */
  locations: Location[];
  defaultLocationId?: number;
}

interface BikeFormData {
  asset_code: string;
  location_id: number;
  bike_type_id: number;
  status: string;
  notes: string;
  send_manager_email: boolean;
}

interface Location {
  id: number;
  name: string;
  city: string;
}

interface BikeType {
  id: number;
  name: string;
  description?: string;
}

const AddBike: React.FC<AddBikeProps> = ({ onBikeAdded, onCancel, locations, defaultLocationId }) => {
  const [formData, setFormData] = useState<BikeFormData>({
    asset_code: '',
    location_id: defaultLocationId || 0,
    bike_type_id: 0,
    status: 'available',
    notes: '',
    send_manager_email: false
  });
  const [bikeTypes, setBikeTypes] = useState<BikeType[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const bikeTypesResponse = await apiClient.get('/bike-types');
      setBikeTypes(bikeTypesResponse.data.data || []);
    } catch (error: any) {
      setError('Er is een fout opgetreden bij het laden van de gegevens.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const { checked } = e.target as HTMLInputElement;
      setFormData(prev => ({ ...prev, [name]: checked }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: name === 'location_id' || name === 'bike_type_id' ? parseInt(value) : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await apiClient.post('/bikes', formData);
      onBikeAdded();
    } catch (error: any) {
      const forbidden = permissionMessage(error);
      if (forbidden) {
        setError(forbidden);
      } else if (error.response?.data?.details) {
        setError(error.response.data.details.join(', '));
      } else {
        setError('Er is een fout opgetreden bij het toevoegen van de fiets.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="add-bike-form">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Gegevens laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="add-bike-form">
      <div className="form-header">
        <h3>Nieuwe Fiets Toevoegen</h3>
        <button 
          className="close-btn"
          onClick={onCancel}
          type="button"
        >
          ×
        </button>
      </div>

      <form onSubmit={handleSubmit} className="bike-form">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="asset_code" className="required">
            Asset Code
          </label>
          <input
            type="text"
            id="asset_code"
            name="asset_code"
            value={formData.asset_code}
            onChange={handleInputChange}
            required
            placeholder="Bijv. F001, B-2024-001"
            className="form-input"
            maxLength={50}
          />
          <small className="form-help">
            Unieke identificatiecode voor de fiets
          </small>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="location_id" className="required">
              Locatie
            </label>
            <select
              id="location_id"
              name="location_id"
              value={formData.location_id}
              onChange={handleInputChange}
              required
              className="form-select"
            >
              <option value={0}>Selecteer een locatie</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name} - {location.city}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="bike_type_id" className="required">
              Fiets Type
            </label>
            <select
              id="bike_type_id"
              name="bike_type_id"
              value={formData.bike_type_id}
              onChange={handleInputChange}
              required
              className="form-select"
            >
              <option value={0}>Selecteer een type</option>
              {bikeTypes.map((bikeType) => (
                <option key={bikeType.id} value={bikeType.id}>
                  {bikeType.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="status">
            Status
          </label>
          <select
            id="status"
            name="status"
            value={formData.status}
            onChange={handleInputChange}
            className="form-select"
          >
            <option value="available">Beschikbaar</option>
            <option value="maintenance">Onderhoud</option>
            <option value="retired">Buiten gebruik</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="notes">
            Notities
          </label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            placeholder="Optionele notities over de fiets..."
            className="form-textarea"
            rows={3}
            maxLength={500}
          />
          <small className="form-help">
            {formData.notes.length}/500 karakters
          </small>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="send_manager_email"
              checked={formData.send_manager_email}
              onChange={handleInputChange}
            />
            <span>Stuur bevestiging naar beheerder bij reservering</span>
          </label>
          <small className="form-help">
            Bij een nieuwe reservering van deze fiets krijgen de beheerder-e-mailadressen van de locatie een bevestiging.
          </small>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={isSubmitting}
          >
            Annuleren
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting || !formData.asset_code.trim() || formData.location_id === 0 || formData.bike_type_id === 0}
          >
            {isSubmitting ? 'Toevoegen...' : 'Fiets Toevoegen'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddBike;
